import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Avatar, Button, Card } from '@/components/ui';
import { inputClass } from '@/components/forms/form';
import { useAuth } from '@/lib/auth';
import { hueFor, initialsFor } from '@/lib/data/companions';
import {
  useConversations,
  setUnread,
  shortWhen,
  type ConversationSummary,
} from '@/lib/data/conversations';
import {
  getChatSocket,
  openConversation,
  sendMessage,
  markConversationRead,
  leaveConversation,
  setTyping,
  newClientId,
  type ChatMessage,
} from '@/lib/chat';

/**
 * Messages.
 *
 * A list of threads and one open conversation, chosen by `?c=<id>` so a thread
 * is linkable and survives a refresh.
 *
 * A thread exists only once a booking between two people has been confirmed —
 * a trigger creates it (migration 0023). An empty list before that is the
 * correct state, not a failure, and the empty view says so rather than showing
 * an error.
 */
export default function Messages() {
  const { session } = useAuth();
  const me = session?.user.id ?? '';
  const { conversations, loading, error } = useConversations();

  const [params, setParams] = useSearchParams();
  const activeId = params.get('c');

  const active = conversations.find((c) => c.id === activeId) ?? null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <header className="animate-rise mb-6">
        <p className="eyebrow mb-2">Account</p>
        <h1 className="text-3xl font-semibold">Messages</h1>
      </header>

      {error && (
        <Card className="mb-6 px-5 py-4">
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-[var(--muted)]">Loading…</p>
      ) : conversations.length === 0 ? (
        <Card className="px-6 py-12 text-center">
          <h2 className="text-lg font-semibold">No conversations yet</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--ink-2)]">
            You can message a companion once a booking between you is confirmed. Book someone and
            the conversation appears here.
          </p>
          <div className="mt-6">
            <Button to="/companions">Browse companions</Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
          <ThreadList
            conversations={conversations}
            activeId={activeId}
            onPick={(id) => setParams({ c: id })}
          />

          {active ? (
            <Thread key={active.id} conversation={active} me={me} />
          ) : (
            <Card className="grid place-items-center px-6 py-16 text-center">
              <p className="text-sm text-[var(--ink-2)]">Pick a conversation to read it.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function ThreadList({
  conversations,
  activeId,
  onPick,
}: {
  conversations: ConversationSummary[];
  activeId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <Card className="overflow-hidden p-0">
      <ul className="flex flex-col">
        {conversations.map((c) => {
          const on = c.id === activeId;
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onPick(c.id)}
                className="flex w-full items-center gap-3 border-b px-4 py-3 text-left transition-colors"
                style={{
                  borderColor: 'var(--line)',
                  background: on ? 'var(--surface-2)' : 'transparent',
                }}
              >
                <Avatar
                  initials={initialsFor(c.otherName)}
                  hue={hueFor(c.otherId)}
                  size={40}
                  photo={c.otherAvatarUrl ?? undefined}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">{c.otherName}</span>
                    <span className="shrink-0 text-xs text-[var(--muted)]">
                      {shortWhen(c.lastMessageAt)}
                    </span>
                  </span>
                </span>
                {c.unread > 0 && (
                  <span
                    className="ml-1 shrink-0 rounded-full px-2 py-0.5 text-[11px] leading-none font-semibold"
                    style={{ background: 'var(--marigold)', color: '#fff' }}
                  >
                    {c.unread}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Thread({ conversation, me }: { conversation: ConversationSummary; me: string }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'denied'>('loading');
  const [denied, setDenied] = useState('');
  const [theyAreTyping, setTheyAreTyping] = useState(false);
  const [sending, setSending] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Newest at the bottom, so follow it. */
  const scrollDown = useCallback(() => {
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: 'end' }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | undefined;

    void (async () => {
      const ack = await openConversation(conversation.id);
      if (cancelled) return;

      if (!ack.ok) {
        setDenied(ack.message);
        setStatus('denied');
        return;
      }

      setMessages(ack.messages);
      setStatus('ready');
      scrollDown();

      // Opening a thread means reading it.
      void markConversationRead(conversation.id);
      setUnread(conversation.id, 0);

      const socket = await getChatSocket();
      if (!socket || cancelled) return;

      const onNew = (p: { message: ChatMessage; clientId?: string }) => {
        if (p.message.conversationId !== conversation.id) return;

        setMessages((prev) => {
          /*
           * Replace the optimistic copy rather than appending beside it. The
           * server echoes back the clientId we sent, which is the only way to
           * tell "this is the message I just rendered" from "this is a new
           * one that happens to say the same thing".
           */
          const withoutOptimistic = p.clientId
            ? prev.filter((m) => m.id !== p.clientId)
            : prev;

          if (withoutOptimistic.some((m) => m.id === p.message.id)) return withoutOptimistic;
          return [...withoutOptimistic, p.message];
        });

        scrollDown();

        // Reading it as it arrives, since the thread is open on screen.
        if (p.message.senderId !== me) {
          void markConversationRead(conversation.id);
          setUnread(conversation.id, 0);
        }
      };

      const onTyping = (p: { conversationId: string; userId: string; isTyping: boolean }) => {
        if (p.conversationId !== conversation.id || p.userId === me) return;
        setTheyAreTyping(p.isTyping);
      };

      const onRead = (p: { conversationId: string; readerId: string; readAt: string }) => {
        if (p.conversationId !== conversation.id || p.readerId === me) return;
        setMessages((prev) =>
          prev.map((m) => (m.senderId === me && !m.readAt ? { ...m, readAt: p.readAt } : m)),
        );
      };

      socket.on('message_new', onNew);
      socket.on('typing', onTyping);
      socket.on('messages_read', onRead);

      detach = () => {
        socket.off('message_new', onNew);
        socket.off('typing', onTyping);
        socket.off('messages_read', onRead);
      };

      if (cancelled) detach();
    })();

    return () => {
      cancelled = true;
      detach?.();
      void leaveConversation(conversation.id);
    };
  }, [conversation.id, me, scrollDown]);

  async function submit() {
    const body = draft.trim();
    if (!body || sending) return;

    const clientId = newClientId();
    setSending(true);
    setDraft('');

    /*
     * Rendered immediately with the clientId as its id, so the message appears
     * the instant it is sent. The echo above swaps it for the real row. If the
     * send fails it is removed again — a message that silently stays on screen
     * while never having been delivered is the worst outcome here.
     */
    const optimistic: ChatMessage = {
      id: clientId,
      conversationId: conversation.id,
      senderId: me,
      body,
      createdAt: new Date().toISOString(),
      readAt: null,
    };
    setMessages((prev) => [...prev, optimistic]);
    scrollDown();

    void setTyping(conversation.id, false);

    try {
      const ack = await sendMessage(conversation.id, body, clientId);
      if (!ack.ok) {
        setMessages((prev) => prev.filter((m) => m.id !== clientId));
        setDenied(ack.message);
        setStatus(ack.code === 'no_booking' ? 'denied' : 'ready');
        setDraft(body);
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== clientId));
      setDenied(err instanceof Error ? err.message : 'That message did not send.');
      setDraft(body);
    }
    setSending(false);
  }

  function onDraftChange(value: string) {
    setDraft(value);
    void setTyping(conversation.id, value.length > 0);

    // Stop claiming they are typing if they walk away mid-sentence.
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => void setTyping(conversation.id, false), 3000);
  }

  if (status === 'denied') {
    return (
      <Card className="grid place-items-center px-6 py-16 text-center">
        <div>
          <h2 className="text-lg font-semibold">This conversation is closed</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--ink-2)]">{denied}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex h-[32rem] flex-col overflow-hidden p-0">
      <div
        className="flex items-center gap-3 border-b px-4 py-3"
        style={{ borderColor: 'var(--line)' }}
      >
        <Avatar
          initials={initialsFor(conversation.otherName)}
          hue={hueFor(conversation.otherId)}
          size={36}
          photo={conversation.otherAvatarUrl ?? undefined}
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{conversation.otherName}</p>
          {theyAreTyping && <p className="text-xs text-[var(--teal)]">typing…</p>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {status === 'loading' ? (
          <p className="text-sm text-[var(--muted)]">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="mt-8 text-center text-sm text-[var(--muted)]">
            No messages yet. Say hello.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {messages.map((m) => (
              <Bubble key={m.id} message={m} mine={m.senderId === me} />
            ))}
          </ul>
        )}
        <div ref={bottomRef} />
      </div>

      {denied && status === 'ready' && (
        <p className="px-4 pb-2 text-xs" style={{ color: 'var(--danger)' }}>
          {denied}
        </p>
      )}

      <form
        className="flex items-end gap-2 border-t px-3 py-3"
        style={{ borderColor: 'var(--line)' }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <textarea
          className={`${inputClass} max-h-32 min-h-[2.75rem] resize-none rounded-xl border`}
          style={{ borderColor: 'var(--line)' }}
          rows={1}
          value={draft}
          maxLength={4000}
          placeholder="Write a message"
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter is a new line — what every chat does.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <Button type="submit" disabled={sending || draft.trim().length === 0}>
          Send
        </Button>
      </form>
    </Card>
  );
}

function Bubble({ message, mine }: { message: ChatMessage; mine: boolean }) {
  const time = new Date(message.createdAt).toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <li className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[80%] rounded-2xl px-3.5 py-2"
        style={{
          background: mine ? 'var(--marigold)' : 'var(--surface-2)',
          color: mine ? '#fff' : 'var(--ink)',
        }}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
        <p
          className="mt-1 text-right text-[10px] leading-none"
          style={{ color: mine ? 'rgb(255 255 255 / 0.75)' : 'var(--muted)' }}
        >
          {time}
          {mine && message.readAt ? ' · read' : ''}
        </p>
      </div>
    </li>
  );
}
